import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'

import { Button, Link } from '@atoms'
import Badge from '@atoms/Badge'
import { PageLayout } from '@components/layouts/PageLayout'
import { ConfirmDialog } from '@components/molecules/ConfirmDialog'
import EmptyState from '@components/molecules/EmptyState'
import SearchField from '@molecules/SearchField'
import { scopedTranslation } from '@utils/I18n'

import { FaqAccordion } from '../features/faqs/FaqAccordion'
import type { Faq } from '../features/faqs/types'

const t = scopedTranslation('help')

export type HelpProps = {
  faqs: Faq[]
  canManageFaqs: boolean
  supportEmail: string | null
  guidesUrl: string
  videoPlaylistUrl: string | null
  openFaqId: string | null
}

const textOf = (html: string) => new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''

export default function Help({ faqs, canManageFaqs, supportEmail, guidesUrl, videoPlaylistUrl, openFaqId }: HelpProps) {
  const [query, setQuery] = useState('')
  const [deleting, setDeleting] = useState<Faq | null>(null)

  const searchable = useMemo(() => faqs.map((faq) => `${faq.title} ${textOf(faq.contentHtml)}`.toLowerCase()), [faqs])
  const trimmed = query.trim().toLowerCase()
  const visibleFaqs = trimmed ? faqs.filter((_, index) => searchable[index].includes(trimmed)) : faqs

  const moveFaq = (faq: Faq, direction: 'up' | 'down') => {
    const ids = faqs.map((item) => item.id)
    const from = ids.indexOf(faq.id)
    const to = direction === 'up' ? from - 1 : from + 1
    if (to < 0 || to >= ids.length) return
    ;[ids[from], ids[to]] = [ids[to], ids[from]]
    router.patch('/faqs/reorder', { ids }, { preserveScroll: true })
  }

  const deleteFaq = () => {
    if (!deleting) return
    router.delete(`/faqs/${deleting.id}`, { onFinish: () => setDeleting(null) })
  }

  return (
    <PageLayout.Root>
      <PageLayout.Header
        icon="help-circle"
        heading={t('title')}
        subtitle={t('subtitle')}
        actions={
          <>
            {supportEmail && (
              <Link href={`mailto:${supportEmail}`} rank="secondary" leadingIcon="mail-01">
                {t('actions.contact_support')}
              </Link>
            )}
            <Link href={guidesUrl} target="_blank" rel="noopener noreferrer" trailingIcon="external-link">
              {t('actions.view_docs')}
            </Link>
          </>
        }
        search={
          <div className="tw:max-w-xs tw:[&>div>label]:font-bold">
            <SearchField
              fieldName="search"
              displayName={t('search_label')}
              inputWrapperClassName="tw:min-h-12 tw:border-gray-cool-200"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        }
      />
      <PageLayout.Body>
        <div className="tw:flex tw:flex-col tw:gap-10 tw:pt-4">
          <section className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:pb-2">
              <div className="tw:flex tw:items-center tw:gap-2.5">
                <h2 className="tw:text-lg tw:font-bold tw:text-gray-cool-900">{t('faqs.heading')}</h2>
                <Badge color="muted">
                  {trimmed ? t('faqs.count_of', { shown: visibleFaqs.length, total: faqs.length }) : faqs.length}
                </Badge>
              </div>
              {canManageFaqs && (
                <Link as="inertiaLink" href="/faqs/new" rank="secondary" leadingIcon="plus">
                  {t('new_faq')}
                </Link>
              )}
            </div>

            {visibleFaqs.length > 0 ? (
              <FaqAccordion
                faqs={visibleFaqs}
                openFaqId={openFaqId}
                canManage={canManageFaqs}
                onEdit={(faq) => router.visit(`/faqs/${faq.id}/edit`)}
                onMove={moveFaq}
                onDelete={setDeleting}
              />
            ) : (
              <div role="status">
                {faqs.length === 0 ? (
                  <EmptyState
                    iconType="help-circle"
                    headline={t('faqs.empty.headline')}
                    subtext={canManageFaqs ? t('faqs.empty.subtext_admin') : t('faqs.empty.subtext')}
                  />
                ) : (
                  <EmptyState
                    iconType="search-lg"
                    headline={t('faqs.no_results.headline', { query: query.trim() })}
                    subtext={t('faqs.no_results.subtext')}
                  >
                    <Button rank="secondary" onClick={() => setQuery('')}>
                      {t('faqs.no_results.clear')}
                    </Button>
                  </EmptyState>
                )}
              </div>
            )}
          </section>

          {videoPlaylistUrl && (
            <section className="tw:flex tw:flex-col tw:gap-4">
              <h2 className="tw:text-lg tw:font-bold tw:text-gray-cool-900">{t('videos.heading')}</h2>
              <iframe
                src={videoPlaylistUrl}
                title={t('videos.heading')}
                className="tw:aspect-video tw:w-full tw:max-w-3xl tw:rounded-xl tw:border tw:border-gray-cool-200"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </section>
          )}
        </div>
      </PageLayout.Body>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('confirm_delete.title')}
        body={deleting ? t('confirm_delete.body', { title: deleting.title }) : undefined}
        confirmLabel={t('confirm_delete.confirm')}
        confirmIcon="trash-01"
        onConfirm={deleteFaq}
      />
    </PageLayout.Root>
  )
}
